import { Component, h, Prop, Event, EventEmitter } from '@stencil/core';
import { htmlSanitizer } from '../../../utils/html-sanitizer';

@Component({
  tag: 'vsr-product-card',
  styleUrl: 'vsr-product-card.scss',
  shadow: false,
})
export class VsProductCard {
  @Prop() type: string;
  @Prop() imageUrl: string;
  @Prop() imageAltText: string;
  @Prop() borderRadius: string;
  @Prop() animation: string;
  @Prop() imageVariant: string;
  @Prop() cardVariant: string;
  @Prop() imageAspectRatio: string = '3:4';
  @Prop() title: string;
  @Prop() description: string;
  @Prop() cardLink: string;
  @Prop() cardLinkTarget: string = 'same';
  @Prop() buttonText2: string;
  @Prop() theme: 'light' | 'dark' = 'light';
  @Prop() cardCta?: Array<{
    ctaButtonColor: 'primary' | 'secondary' | 'white' | 'link';
    ctaLink: string;
    ctaTitle: string;
    ctaLinkType: 'internal' | 'video' | string;
    ctaLinkTarget: '_self' | 'modal' | string;
    ctaStyle: 'button' | 'link' | string;
  }>;

  @Event() compareClicked: EventEmitter<void>;

  private onCompareClick = (e: Event) => {
    e.preventDefault();
    this.compareClicked.emit();
  };

  private renderCompareIcon() {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <g>
          <path
            d="M10.5 5.5L13 8L10.5 10.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5.5 10.5L3 8L5.5 5.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </svg>
    );
  }

  render() {
    const cardThemeClass = `vsr-product-card-theme-${this.theme}`;
    const hasLinkClass = this.cardLink ? 'has-card-link' : '';
    const aspectRatioClass = `aspect-ratio-${this.imageAspectRatio.replace(':', '-')}`;
    const is16by9 = this.imageAspectRatio === '16:9' ? 'no-hover-16-9' : '';

    const cardContent = (
      <div class="vsr-product-card__content">
        {this.type && <h3 class="vsr-product-card__label">{this.type}</h3>}
        <h3 class="vsr-product-card__title" innerHTML={htmlSanitizer(this.title)} tabindex="0" />
        <p class="vsr-product-card__description" innerHTML={htmlSanitizer(this.description)} />
        {(this.cardCta && this.cardCta.length > 0) || this.buttonText2 ? (
          <div class="vsr-product-card__actions-row">
            {this.cardCta &&
              this.cardCta.length > 0 &&
              this.cardCta.map((btn, index) => {
                const isButton = btn.ctaStyle === 'button';
                const target = btn.ctaLinkTarget === 'new' ? '_blank' : '_self';
                const ComponentTag = isButton ? 'vsr-button-v2' : 'vsr-link-button-v2';
                return (
                  <ComponentTag
                    key={index}
                    href={btn.ctaLink || '#'}
                    variant={btn.ctaButtonColor || 'primary'}
                    themeOverride={this.theme}
                    target={target}
                    aria-label={btn.ctaTitle}
                    ctaLinkType={btn.ctaLinkType}
                  >
                    <span innerHTML={htmlSanitizer(btn.ctaTitle)}></span>
                  </ComponentTag>
                );
              })}
            {this.buttonText2 && (
              <a
                href="#"
                onClick={this.onCompareClick}
                class="vsr-product-card__action-button compare"
              >
                {this.buttonText2} {this.renderCompareIcon()}
              </a>
            )}
          </div>
        ) : null}
      </div>
    );

    if (this.cardLink) {
      return (
        <div class={`vsr-product-card ${cardThemeClass} ${hasLinkClass} ${is16by9}`}>
          <a
            href={this.cardLink}
            target={this.cardLinkTarget === 'new' ? '_blank' : '_self'}
            class="vsr-product-card__link"
            alt-text={this.imageAltText || this.title}
          >
            <div class={`vsr-product-card__fixed-image ${aspectRatioClass}`}>
              <img
                class="vsr-produc-card__image"
                src={this.imageUrl}
                alt={this.imageAltText || this.title}
              />
            </div>
            {cardContent}
          </a>
        </div>
      );
    } else {
      return (
        <div class={`vsr-product-card ${cardThemeClass} ${hasLinkClass} ${is16by9}`}>
          <div class={`vsr-product-card__fixed-image ${aspectRatioClass}`}>
            <img
              class="vsr-produc-card__image"
              src={this.imageUrl}
              alt={this.imageAltText || this.title}
            />
          </div>
          {cardContent}
        </div>
      );
    }
  }
}
